// src/views/menage/ControleListeLarge.jsx — LES CARTES « À CONTRÔLER »
// (recalage validé Dimitri 2026-08-21 — REMPLACE les lignes ET l'ancien
// dessin mobile : une seule forme de carte partout).
//
//   · ⚠️ « RIEN À SIGNALER » A DISPARU DE LA LISTE (mobile compris) : le
//     raccourci invitait à valider sans regarder. UN SEUL bouton d'action —
//     « OUVRIR », dégradé navy 115°, 42 px. Le verdict reste à un clic
//     DERRIÈRE, après la checklist et la contrainte voyageur.
//   · GRILLE auto (colonnes ≥ 238 px, écart 10) ≥ 900 px — un groupe à une
//     carte ne laisse pas de trou ; UNE COLONNE dessous, mêmes cartes.
//   · CARTE : pastille 34 r10 initiale · nom 13.5 gras (tronqué proprement) ·
//     « Karim · 11h30 · 17/18 » 10.5 · urgence 11 · QUI CONTRÔLE 36 px
//     (« Sofia contrôle » bordé vert / « Désigner » pointillés ambre centré) ·
//     « Ouvrir » dégradé + itinéraire 42 px. Ça presse : fond #fdf9f7 bordé
//     #e0b5a5.
//   · SON PROPRE MÉNAGE : pointillés #d5e3ee sur #f8fafc, gris, cadenas
//     « votre ménage », bouton « À faire contrôler » GRISÉ non cliquable —
//     « Désigner » et l'itinéraire restent (le serveur refuse aussi, 403).
//
// Le BANDEAU à trois blocs filtrants (presse / en attente / sans contrôleur)
// ne change pas — orchestrateur/admin seulement (prop `chiffres`).

import { useState, useEffect, useRef } from 'react'
import PortailFeuille from '../../components/PortailFeuille'
import useVoileEcran from '../../lib/useVoileEcran'
import useEtroit from '../../lib/useEtroit'
import { DS } from '../../lib/ui'
import CarteTitrePage from '../../components/CarteTitrePage'
import {
  AlarmClock, Eye, UserRoundSearch, ChevronDown, ChevronRight, Navigation,
  Lock, CalendarDays,
} from 'lucide-react'

const CIEL = '#5c8ab8'
const AMBRE = '#d4a24c'
const TERRA_SOMBRE = '#8f2f1d'
const VERT_BORD = '#c9e0d4'
const NAVY_DEGRADE = 'linear-gradient(115deg, #3d5166, #5c8ab8)'

function initiales(texte) {
  return String(texte || '?').slice(0, 2).toUpperCase()
}
// « 21 août » — la règle des dates (ni jour de semaine, ni année).
function dateCourte(jourIso) {
  const t = jourIso ? Date.parse(`${jourIso}T12:00:00Z`) : Date.now()
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', timeZone: 'Europe/Paris' })
    .format(t)
}
// « mardi 18 » — l'en-tête du menu de désignation (maquette).
function dateMenu(iso) {
  const t = Date.parse(`${String(iso || '').slice(0, 10)}T12:00:00Z`)
  if (Number.isNaN(t)) return ''
  return new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', timeZone: 'Europe/Paris' }).format(t)
}
function heureFRLocale(ts) {
  if (!ts) return null
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return null
  return new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'Europe/Paris' })
    .format(d).replace(':', 'h')
}

// ── LE MENU DE DÉSIGNATION — inchangé (les deux portées) ────────────────────
function MenuDesignation({ menage, nomLogement, agents, selfEmail, onChoisir, onFermer }) {
  useVoileEcran()
  // TÉLÉPHONE (cadrage 2026-08-22) : LA DÉSIGNATION MONTE DU BAS.
  const etroit = useEtroit()
  const [parDefaut, setParDefaut] = useState(false)
  const racineRef = useRef(null)
  useEffect(() => {
    const clicDehors = (e) => {
      if (racineRef.current && !racineRef.current.contains(e.target)) onFermer()
    }
    const echap = (e) => { if (e.key === 'Escape') onFermer() }
    const t = setTimeout(() => document.addEventListener('pointerdown', clicDehors, true), 0)
    document.addEventListener('keydown', echap)
    return () => { clearTimeout(t); document.removeEventListener('pointerdown', clicDehors, true); document.removeEventListener('keydown', echap) }
  }, [onFermer])

  const ligne = (contenu, onClic, testId) => (
    <button key={testId} type="button" className="fbz-press" data-testid={testId}
      onClick={onClic}
      style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', minHeight: 46,
        background: 'none', border: 'none', padding: '4px 12px', cursor: 'pointer',
        textAlign: 'left', fontFamily: DS.fontBody }}>
      {contenu}
    </button>
  )

  return (<PortailFeuille actif={etroit}><>
    {etroit && (
      <div onClick={onFermer || undefined}
        style={{ position: 'fixed', inset: 0, background: 'rgba(46,61,77,.4)', zIndex: 998 }} />
    )}
    <div ref={racineRef} data-testid={`menu-designation-${menage.run_id}`}
      style={etroit
        ? { position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 999, background: '#fff',
            borderRadius: '18px 18px 0 0', boxShadow: '0 -8px 28px rgba(33,83,135,.25)',
            maxHeight: '72vh', overflowY: 'auto',
            paddingBottom: 'env(safe-area-inset-bottom)' }
        : { position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 40,
        minWidth: 240, background: '#fff', borderRadius: 12, border: '1px solid #d5e3ee',
        boxShadow: '0 10px 30px rgba(35,50,64,.18)', overflow: 'hidden' }}>
      {etroit && (
        <div aria-hidden="true" style={{ width: 36, height: 4, borderRadius: 2,
          background: '#d5e3ee', margin: '8px auto 2px' }} />
      )}
      <div style={{ background: '#f4faf7', padding: '10px 12px', display: 'flex',
        alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: etroit ? 14.5 : 10, fontWeight: 700,
          letterSpacing: etroit ? 0 : '.07em', color: etroit ? '#233240' : '#1a4f3a',
          textTransform: etroit ? 'none' : 'uppercase' }}>Qui contrôle</div>
        <div style={{ fontSize: 11.5, color: '#2e3d4d', marginTop: 2 }}>
          {nomLogement} · {dateMenu(menage.scheduled_date)}
        </div>
        </div>
        {etroit && onFermer && (
          <button type="button" aria-label="Fermer" onClick={onFermer} className="fbz-press"
            style={{ width: 32, height: 32, borderRadius: 9, border: 'none',
              background: '#f4f6f8', color: '#5c6773', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
            ✕
          </button>
        )}
      </div>

      {agents.map((a) => ligne(
        <>
          <span aria-hidden="true" style={{ width: 28, height: 28, borderRadius: 9, background: CIEL,
            color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{initiales(a.prenom)}</span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#233240' }}>
              {a.prenom}
            </span>
            {menage.defaut_email && a.email === menage.defaut_email && (
              <span data-testid={`menu-habituel-${menage.run_id}`}
                style={{ display: 'block', fontSize: 10, color: DS.textSecondary }}>
                contrôleur habituel
              </span>
            )}
          </span>
        </>,
        () => onChoisir({ email: a.email, parDefaut }),
        `menu-agent-${menage.run_id}-${a.prenom}`,
      ))}

      {/* « Je contrôle moi-même » RETIRÉ (correction Dimitri 2026-08-21) :
          l'orchestrateur contrôle TOUJOURS par « Ouvrir », désigné ou pas —
          le menu ne sert qu'à désigner QUELQU'UN D'AUTRE. */}

      <label data-testid={`menu-case-defaut-${menage.run_id}`}
        style={{ display: 'flex', alignItems: 'flex-start', gap: 9, background: '#f8fafc',
          padding: '10px 12px', cursor: 'pointer', borderTop: '1px solid #eef1f4' }}>
        <input type="checkbox" checked={parDefaut}
          onChange={(e) => setParDefaut(e.target.checked)}
          style={{ marginTop: 2, width: 15, height: 15, accentColor: '#3d5166' }} />
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: '#233240', lineHeight: 1.4 }}>
            Faire de cette personne le contrôleur habituel de {nomLogement}
          </span>
          <span style={{ display: 'block', fontSize: 10.5, color: DS.textSecondary, marginTop: 2 }}>
            Tous les prochains contrôles de ce logement lui reviendront.
          </span>
        </span>
      </label>
    </div>
  </></PortailFeuille>)
}

// ── UNE CARTE ───────────────────────────────────────────────────────────────
function CarteMenage({ menage, nomLogement, urgence, agents, selfEmail, peutDesigner,
  nomProduitDe, onOuvrir, onDesigner }) {
  const [menuOuvert, setMenuOuvert] = useState(false)
  const verrouille = menage.est_mon_menage === true
  const finiA = heureFRLocale(menage.completed_at)
  const aCompte = typeof menage.coches === 'number' && typeof menage.total === 'number'
  const sousTitre = [
    menage.agent_prenom || 'Agent',
    finiA,
    // ⚠️ DEUX VIDES OPPOSÉS (chantier Dimitri 2026-08-22) : sans checklist
    // CONFIGURÉE sur le logement, la carte écrit « — » (rien à cocher, trou de
    // paramétrage) ; configurée mais rien de coché, elle chiffre le manquement
    // (« 0/25 »). `lignes_configurees` vient du serveur.
    aCompte
      ? (Number(menage.lignes_configurees) > 0
        ? `${menage.coches}/${Math.max(menage.total, menage.lignes_configurees)}`
        : (menage.total > 0 ? `${menage.coches}/${menage.total}` : '—'))
      : null,
  ].filter(Boolean).join(' · ')

  return (
    <div data-testid={`controle-carte-${menage.run_id}`}
      data-verrouille={verrouille ? '1' : '0'}
      style={{
        display: 'flex', flexDirection: 'column', gap: 9, borderRadius: 12,
        padding: '12px 13px',
        ...(verrouille
          ? { background: '#f8fafc', border: '1.5px dashed #d5e3ee' }
          : urgence?.presse
            ? { background: '#fdf9f7', border: '1px solid #e0b5a5' }
            : { background: '#fff', border: '1px solid #e3e8ee' }),
      }}>
      {/* Pastille + nom + sous-titre. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <span aria-hidden="true" style={{ width: 34, height: 34, borderRadius: 10,
          background: verrouille ? '#e3e8ee' : (urgence?.presse ? '#b8412b' : '#e4f0fb'),
          color: verrouille ? '#98a2ad' : (urgence?.presse ? '#fff' : '#3d5166'),
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, fontWeight: 700, fontFamily: DS.fontDisplay, flexShrink: 0 }}>
          {String(nomLogement).charAt(0).toUpperCase()}
        </span>
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700,
            color: verrouille ? '#98a2ad' : '#233240',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {nomLogement}
          </span>
          <span style={{ display: 'block', fontSize: 10.5,
            color: verrouille ? '#b4bcc5' : DS.textSecondary,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {sousTitre}
          </span>
        </span>
        {/* TERRAIN : « Pour vous » quand ce contrôle lui est désigné — le
            pilotage a son bouton QUI CONTRÔLE à la place. */}
        {!peutDesigner && menage.designe_pour_moi === true && (
          <span data-testid={`controle-designe-${menage.run_id}`}
            style={{ flexShrink: 0, fontSize: 10, fontWeight: 700, background: '#e4f0fb',
              color: '#3d5166', borderRadius: 6, padding: '2px 7px',
              textTransform: 'uppercase', letterSpacing: '.03em' }}>
            Pour vous
          </span>
        )}
      </div>

      {/* Urgence — ou le cadenas sur sa propre carte. */}
      {verrouille ? (
        <div data-testid={`controle-verrou-${menage.run_id}`}
          style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11,
            fontStyle: 'italic', color: '#98a2ad' }}>
          <Lock size={12} strokeWidth={2.2} /> votre ménage
        </div>
      ) : urgence && (
        <div data-testid={`controle-urgence-${menage.run_id}`}
          style={{ fontSize: 11, fontWeight: urgence.presse ? 700 : 400,
            color: urgence.presse ? TERRA_SOMBRE : (urgence.manque ? '#8a5f19' : CIEL) }}>
          {urgence.liste}
        </div>
      )}

      {/* LE MANQUE SIGNALÉ « POUR AUJOURD'HUI » — un FAIT terrain, terracotta
          (le voyageur arrive sans : à corriger avant l'arrivée). */}
      {(menage.manques_aujourdhui || []).length > 0 && (
        <div data-testid={`controle-manque-${menage.run_id}`}>
          <div style={{ fontSize: 11, color: TERRA_SOMBRE }}>
            Il manque pour cette arrivée : <strong>{(menage.manques_aujourdhui || [])
              .map((m) => (m.cs_id ? (nomProduitDe ? nomProduitDe(m.cs_id) : m.cs_id) : m.texte))
              .filter(Boolean).join(', ')}</strong>
          </div>
          <div style={{ fontSize: 9.5, color: '#98a2ad', marginTop: 1 }}>
            signalé par {[...new Set((menage.manques_aujourdhui || []).map((m) => m.par_prenom).filter(Boolean))].join(', ')}
          </div>
        </div>
      )}

      {/* QUI CONTRÔLE — seulement quand la donnée existe (pilotage). */}
      {peutDesigner && (
        <span style={{ position: 'relative', display: 'block' }}>
          {menage.controleur_prenom ? (
            <button type="button" data-testid={`controle-qui-${menage.run_id}`}
              onClick={() => setMenuOuvert((v) => !v)} className="fbz-press"
              style={{ display: 'flex', alignItems: 'center', gap: 7, width: '100%',
                minHeight: 36, padding: '0 10px', background: '#fff',
                border: `1px solid ${VERT_BORD}`, borderRadius: 9, cursor: 'pointer',
                fontFamily: DS.fontBody }}>
              <span aria-hidden="true" style={{ width: 22, height: 22, borderRadius: 7,
                background: CIEL, color: '#fff', display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 9.5, fontWeight: 700, flexShrink: 0 }}>
                {initiales(menage.controleur_prenom)}
              </span>
              <span style={{ flex: 1, minWidth: 0, textAlign: 'left', fontSize: 11.5,
                fontWeight: 600, color: '#233240', overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {menage.controleur_prenom} contrôle
              </span>
              <ChevronDown size={13} strokeWidth={2.4} color="#98a2ad" style={{ flexShrink: 0 }} />
            </button>
          ) : (
            <button type="button" data-testid={`controle-designer-${menage.run_id}`}
              onClick={() => setMenuOuvert((v) => !v)} className="fbz-press"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                width: '100%', minHeight: 36, background: '#fdf9f7',
                border: `1.5px dashed ${AMBRE}`, borderRadius: 9, color: '#8a5f19',
                fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: DS.fontBody }}>
              <UserRoundSearch size={14} strokeWidth={2.2} /> Désigner
            </button>
          )}
          {menuOuvert && (
            <MenuDesignation menage={menage} nomLogement={nomLogement} agents={agents}
              selfEmail={selfEmail}
              onChoisir={(choix) => { setMenuOuvert(false); onDesigner(menage, choix) }}
              onFermer={() => setMenuOuvert(false)} />
          )}
        </span>
      )}

      {/* L'ACTION — UN SEUL bouton : « Ouvrir » en dégradé navy. Sur sa
          propre carte : « À faire contrôler » grisé, non cliquable. */}
      <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
        {verrouille ? (
          <button type="button" data-testid={`controle-a-faire-${menage.run_id}`} disabled
            style={{ flex: 1, minHeight: 42, display: 'inline-flex', alignItems: 'center',
              justifyContent: 'center', gap: 6, background: '#eef1f4', border: 'none',
              borderRadius: 9, color: '#98a2ad', fontSize: 12.5, fontWeight: 700,
              fontFamily: DS.fontBody, cursor: 'not-allowed' }}>
            À faire contrôler
          </button>
        ) : (
          <button type="button" data-testid={`controle-ouvrir-${menage.run_id}`}
            onClick={() => onOuvrir(menage)} className="fbz-press"
            style={{ flex: 1, minHeight: 42, display: 'inline-flex', alignItems: 'center',
              justifyContent: 'center', gap: 6, background: NAVY_DEGRADE, border: 'none',
              borderRadius: 9, color: '#fff', fontSize: 12.5, fontWeight: 700,
              fontFamily: DS.fontBody, cursor: 'pointer' }}>
            Ouvrir <ChevronRight size={14} strokeWidth={2.4} />
          </button>
        )}
        {menage.adresse && (
          <a data-testid={`controle-itineraire-${menage.run_id}`}
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(menage.adresse)}`}
            target="_blank" rel="noopener noreferrer" className="fbz-press"
            aria-label={`Itinéraire vers ${nomLogement}`}
            style={{ width: 42, minHeight: 42, display: 'inline-flex', alignItems: 'center',
              justifyContent: 'center', background: '#fff', border: `1px solid ${CIEL}`,
              borderRadius: 9, color: CIEL, textDecoration: 'none', flexShrink: 0 }}>
            <Navigation size={16} strokeWidth={2.2} />
          </a>
        )}
      </div>
    </div>
  )
}

// ── L'ÉCRAN ─────────────────────────────────────────────────────────────────
export default function ControleListeLarge({ sousBandeau = null, menages, nbFinis, urgenceDe, nomDe,
  agents, selfEmail, peutDesigner, chiffres, estLarge, sousTitreSimple,
  onOuvrir, onDesigner, differes = {}, onAnnuler, jour, consultation = false, onJour,
  nomProduitDe }) {
  const [filtre, setFiltre] = useState(null)

  const avecUrgence = menages.map((m) => ({ m, urg: urgenceDe(m) }))
  const presse = avecUrgence.filter((x) => x.urg?.presse)
  const sansControleur = avecUrgence.filter((x) => !x.m.controleur_prenom)
  const prochaine = presse
    .map((x) => x.urg?.dansHeures).filter((h) => h != null).sort((a, b) => a - b)[0]

  const BLOCS = [
    { cle: 'presse', Ico: AlarmClock, fond: '#b8412b',
      compte: `${presse.length} presse${presse.length > 1 ? 'nt' : ''}`,
      precision: prochaine != null ? `arrivée dans ${prochaine} h` : null,
      couleur: '#f5c4b4' },
    { cle: 'attente', Ico: Eye, fond: CIEL,
      compte: `${menages.length} en attente`,
      precision: `sur ${nbFinis} fini${nbFinis > 1 ? 's' : ''}`,
      couleur: 'rgba(255,255,255,.75)' },
    { cle: 'sans-controleur', Ico: UserRoundSearch, fond: AMBRE,
      compte: `${sansControleur.length} sans contrôleur`,
      precision: sansControleur.length ? 'à désigner' : null,
      couleur: '#f5d9a8' },
  ]

  const garde = (x) => {
    if (filtre === 'presse') return x.urg?.presse
    if (filtre === 'sans-controleur') return !x.m.controleur_prenom
    return true
  }
  // « Ça presse » s'ordonne par HEURE D'ARRIVÉE (décision 2026-08-18 — le
  // contrôleur se déplace) ; « Sans urgence » garde l'ordre de fin de ménage.
  const heureArrMin = (m) => {
    const x = /^(\d{1,2}):(\d{2})/.exec(String(m.prochaine_arrivee?.heure || ''))
    return x ? Number(x[1]) * 60 + Number(x[2]) : 24 * 60
  }
  const groupePresse = avecUrgence.filter((x) => x.urg?.presse && garde(x))
    .sort((a, b) => heureArrMin(a.m) - heureArrMin(b.m))
  const groupeCalme = avecUrgence.filter((x) => !x.urg?.presse && garde(x))

  // LA GRILLE — colonnes ≥ 238 px ; une COLONNE sous 900 px.
  const grille = estLarge
    ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(238px, 1fr))', gap: 10 }
    : { display: 'flex', flexDirection: 'column', gap: 10 }

  const carteDe = ({ m, urg }) => (
    <CarteMenage key={m.run_id} menage={m} nomLogement={nomDe(m)} urgence={urg}
      agents={agents} selfEmail={selfEmail} peutDesigner={peutDesigner}
      nomProduitDe={nomProduitDe} onOuvrir={onOuvrir} onDesigner={onDesigner} />
  )

  return (
    <div data-testid="controle-liste-cartes">
      {chiffres ? (
        <CarteTitrePage
          testId="controle-bandeau-liste"
          titre={(
            <span style={{ fontSize: 20 }}>
              À contrôler{' '}
              <span data-testid="controle-bandeau-date" style={{ fontSize: 11.5, fontWeight: 400,
                fontFamily: DS.fontBody, color: 'rgba(255,255,255,.75)' }}>
                {dateCourte(jour)}
              </span>
            </span>
          )}
          actions={onJour && (
            <span style={{ display: 'inline-flex', gap: 6, flexShrink: 0 }}>
              <button type="button" data-testid="controle-jour-prec" aria-label="Jour précédent"
                onClick={() => onJour(-1)} className="fbz-press"
                style={{ width: 32, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: 'rgba(255,255,255,.14)', color: '#fff', fontSize: 15, fontWeight: 700 }}>‹</button>
              <button type="button" data-testid="controle-jour-suiv" aria-label="Jour suivant"
                onClick={() => onJour(1)} disabled={!consultation}
                className="fbz-press"
                style={{ width: 32, height: 32, borderRadius: 8, border: 'none',
                  cursor: consultation ? 'pointer' : 'not-allowed',
                  background: 'rgba(255,255,255,.14)', color: '#fff', fontSize: 15, fontWeight: 700,
                  opacity: consultation ? 1 : .45 }}>›</button>
            </span>
          )}
          sousLeFilet={consultation ? (
            <div data-testid="controle-consultation-bandeau"
              style={{ fontSize: 11.5, color: 'rgba(255,255,255,.85)' }}>
              Journée passée — consultation seule : les gestes vivent sur le jour même.
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'stretch', gap: 10, flexWrap: 'wrap' }}>
              {BLOCS.map(({ cle, Ico, fond, compte, precision, couleur }) => {
                const actif = filtre === cle
                return (
                  <button key={cle} type="button" className="fbz-press"
                    data-testid={`controle-filtre-${cle}`} aria-pressed={actif}
                    onClick={() => setFiltre((f) => (f === cle ? null : cle))}
                    style={{ display: 'flex', alignItems: 'center', gap: 9, minHeight: 44,
                      padding: '6px 12px', borderRadius: 10, border: 'none',
                      background: actif ? 'rgba(255,255,255,.22)' : 'rgba(255,255,255,.13)',
                      boxShadow: actif ? 'inset 0 0 0 1px rgba(255,255,255,.3)' : 'none',
                      cursor: 'pointer', textAlign: 'left', fontFamily: DS.fontBody }}>
                    <span aria-hidden="true" style={{ width: 30, height: 30, borderRadius: 9,
                      background: fond, flexShrink: 0, display: 'inline-flex',
                      alignItems: 'center', justifyContent: 'center' }}>
                      <Ico size={16} strokeWidth={2.2} color="#fff" />
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: '#fff' }}>
                        {compte}
                      </span>
                      {precision && (
                        <span style={{ display: 'block', fontSize: 10, marginTop: 1, color: couleur }}>
                          {precision}
                        </span>
                      )}
                    </span>
                  </button>
                )
              })}
            </div>
          )}
        />
      ) : (
        <CarteTitrePage titre="À contrôler" sousTitre={sousTitreSimple}
          testId="controle-bandeau-liste"
          actions={onJour && (
            <span style={{ display: 'inline-flex', gap: 6, flexShrink: 0 }}>
              <button type="button" data-testid="controle-jour-prec" aria-label="Jour précédent"
                onClick={() => onJour(-1)} className="fbz-press"
                style={{ width: 32, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: 'rgba(255,255,255,.14)', color: '#fff', fontSize: 15, fontWeight: 700 }}>‹</button>
              <button type="button" data-testid="controle-jour-suiv" aria-label="Jour suivant"
                onClick={() => onJour(1)} disabled={!consultation}
                className="fbz-press"
                style={{ width: 32, height: 32, borderRadius: 8, border: 'none',
                  cursor: consultation ? 'pointer' : 'not-allowed',
                  background: 'rgba(255,255,255,.14)', color: '#fff', fontSize: 15, fontWeight: 700,
                  opacity: consultation ? 1 : .45 }}>›</button>
            </span>
          )} />
      )}

      {/* LA BASCULE DE PAGE (téléphone) — SOUS le bandeau, UNE seule fois
          (correction Dimitri 22/08 : elle avait été insérée par erreur DANS la
          pastille d'icône de CHAQUE bloc du bandeau → N copies superposées,
          textes entrelacés « Demandes1Incidents4Contrôle »). */}
      {sousBandeau}

      {/* L'ANNULATION DIFFÉRÉE (décision conservée) : le verdict « rien à
          signaler » pris DEPUIS L'ÉCRAN revient ici en bande verte annulable —
          l'envoi ne part qu'après le délai. */}
      {Object.values(differes).map((m) => (
        <div key={m.run_id} data-testid={'controle-differe-' + m.run_id}
          style={{ background: '#f0f7f3', border: '1.5px solid #7fc4a3', borderRadius: 11,
            padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 10,
            marginBottom: 10 }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: '#1a4f3a' }}>
            {nomDe(m)} — rien à signaler. Envoi dans un instant…
          </span>
          <button type="button" data-testid={'controle-annuler-' + m.run_id}
            onClick={() => onAnnuler && onAnnuler(m.run_id)} className="fbz-press"
            style={{ flexShrink: 0, minHeight: 38, padding: '0 14px', background: '#fff',
              border: '1px solid #c9e0d4', borderRadius: 9, color: '#1a4f3a',
              fontSize: 12, fontWeight: 700, fontFamily: DS.fontBody, cursor: 'pointer' }}>
            Annuler
          </button>
        </div>
      ))}

      {!consultation && menages.length === 0 && (
        <div data-testid="controle-liste-vide"
          style={{ padding: '26px 16px', textAlign: 'center', background: '#fff',
            border: '1px solid #d5e3ee', borderRadius: 12, fontSize: 13.5,
            color: DS.textSecondary }}>
          Aucun ménage à contrôler pour l'instant.
        </div>
      )}

      {groupePresse.length > 0 && (
        <div data-testid="controle-groupe-presse" style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10.5,
            fontWeight: 700, color: TERRA_SOMBRE, textTransform: 'uppercase',
            letterSpacing: '.08em', margin: '0 2px 8px' }}>
            <AlarmClock size={13} strokeWidth={2.4} /> Ça presse
          </div>
          <div style={grille}>{groupePresse.map(carteDe)}</div>
        </div>
      )}
      {groupeCalme.length > 0 && (
        <div data-testid="controle-groupe-sans-urgence">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10.5,
            fontWeight: 700, color: '#98a2ad', textTransform: 'uppercase',
            letterSpacing: '.08em', margin: '0 2px 8px' }}>
            <CalendarDays size={13} strokeWidth={2.2} /> À faire aujourd'hui
          </div>
          <div style={grille}>{groupeCalme.map(carteDe)}</div>
        </div>
      )}
      {filtre && groupePresse.length === 0 && groupeCalme.length === 0 && menages.length > 0 && (
        <div data-testid="controle-filtre-vide"
          style={{ padding: '22px 16px', textAlign: 'center', background: '#fff',
            border: '1px solid #d5e3ee', borderRadius: 12, fontSize: 13,
            color: DS.textSecondary }}>
          {filtre === 'presse' ? 'Rien ne presse.' : filtre === 'sans-controleur'
            ? 'Tous les ménages ont leur contrôleur.' : 'Rien en attente.'}
        </div>
      )}
    </div>
  )
}
